import { useApi } from '@/api';
import { Carousel, EntityName, LinkCard, ProgressBar } from '@/components';
import { useSparkColor } from '@/hooks';
import {
  ASSET_SYMBOLS_PALETTE,
  Box,
  CryptoIcon,
  CurrencyValue,
  Flex,
  FlexProps,
  NetworkIcon,
  PercentageValue,
  TableSkeleton,
  Text
} from '@blockanalitica/ui';
import styled, { css } from 'styled-components';

type VaultsData = {
  symbol: string;
  address: string;
  total_supply: string;
  circulating_supply: string;
  networks: string[];
  rate: string;
  underlying_address: string;
  underlying_symbol: string;
  description: string;
  percentage: string;
};

const KpiTitle = styled(Flex)<FlexProps>`
  ${(props) => css`
    color: ${props.theme.colors.muted};
    font-size: ${props.theme.sizes['-1']};
  `}
`;

export default function SavingsVaults() {
  const color = useSparkColor();
  const [data, error] = useApi<VaultsData[]>({
    url: '/sparkstar/savings/vaults/'
  });

  if (error) {
    throw error;
  }
  if (!data) {
    // TODO: wrong skeleton
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <Flex flexDirection="column" gap="xs">
      <Carousel>
        {data.map((row) => (
          <LinkCard
            to={`/savings/${row.symbol}`}
            key={row.symbol}
            gap="m"
            justifyContent="space-between"
            flexDirection="column"
            hoverBorderColor={color}>
            <Flex flexDirection="column" gap="m">
              <EntityName
                symbol2={
                  <CryptoIcon
                    name={row.symbol}
                    address={row.address}
                    size="5"
                  />
                }
                symbol1={
                  <CryptoIcon
                    name={row.underlying_symbol}
                    address={row.underlying_address}
                    size="5"
                  />
                }
                entitySize="5">
                <Flex flexDirection="column" gap="4xs">
                  <Box as="span" fontSize="1">
                    {row.symbol}
                  </Box>
                  <Box as="span" color="muted" fontSize="-1">
                    {row.description}
                  </Box>
                </Flex>
              </EntityName>

              <Flex gap="xs">
                <Flex flexDirection="column" flex="1" gap="3xs">
                  <KpiTitle>Deposits</KpiTitle>
                  <CurrencyValue
                    value={row.circulating_supply}
                    options={{ currency: 'USD' }}
                    fontSize="4"
                    variant="dynamic"
                  />
                </Flex>
                <Box fontSize="-1" marginLeft="auto">
                  <KpiTitle color="muted" fontSize="-1">
                    APY
                  </KpiTitle>

                  <PercentageValue
                    value={row.rate}
                    fontSize="4"
                    variant="dynamic"
                  />
                </Box>
              </Flex>
            </Flex>
            <ProgressBar
              color={ASSET_SYMBOLS_PALETTE[row.symbol] || '#FFFFFF'}
              percent={Number(row.percentage)}
              text={
                <Text color="muted" size="2">
                  % of total deposits
                </Text>
              }
              height="-4"
              muted={true}
            />
            <Flex justifyContent="space-between">
              <Flex color="muted" gap="3xs" fontSize="-1">
                {row.networks.map((network: string) => (
                  <NetworkIcon network={network} key={network} />
                ))}
              </Flex>

              <Box fontSize="-1" color="link">
                View Details
              </Box>
            </Flex>
          </LinkCard>
        ))}
      </Carousel>
    </Flex>
  );
}
